import { CorrespondenceDepartmentFunctionDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as CorrespondenceFunctionActions from './function.actions';

export const CORRESPONDENCE_FUNCTION_FEATURE_KEY = 'correspondenceFunction';

export interface CorrespondenceFunctionState
  extends EntityState<CorrespondenceDepartmentFunctionDto> {
  selectedCorrespondenceFunctionId?: string | number; // which CorrespondenceFunction record has been selected
  loaded: boolean; // has the CorrespondenceFunction list been loaded
  error?: string | null; // last none error (if any)
}

export interface CorrespondenceFunctionPartialState {
  readonly [CORRESPONDENCE_FUNCTION_FEATURE_KEY]: CorrespondenceFunctionState;
}

export const correspondenceFunctionAdapter: EntityAdapter<CorrespondenceDepartmentFunctionDto> = createEntityAdapter<
  CorrespondenceDepartmentFunctionDto
>({
  selectId: (correspondenceFunction) => correspondenceFunction.id,
  sortComparer: false,
});

export const initialState: CorrespondenceFunctionState = correspondenceFunctionAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedCorrespondenceFunctionId: null,
  }
);

const correspondenceFunctionReducer = createReducer(
  initialState,
  on(CorrespondenceFunctionActions.loadCorrespondenceFunction, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    CorrespondenceFunctionActions.loadCorrespondenceFunctionSuccess,
    (state, { correspondenceFunction }) =>
      correspondenceFunctionAdapter.addAll(correspondenceFunction, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceFunctionActions.loadCorrespondenceFunctionFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(CorrespondenceFunctionActions.addCorrespondenceFunction, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    CorrespondenceFunctionActions.addCorrespondenceFunctionSuccess,
    (state, { newCorrespondenceFunction }) =>
      correspondenceFunctionAdapter.addMany(newCorrespondenceFunction, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceFunctionActions.addCorrespondenceFunctionFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(CorrespondenceFunctionActions.updateCorrespondenceFunction, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    CorrespondenceFunctionActions.updateCorrespondenceFunctionSuccess,
    (state, { updateCorrespondenceFunction }) =>
      correspondenceFunctionAdapter.upsertMany(updateCorrespondenceFunction, {
        ...state,
        loaded: false,
      })
  ),
  on(
    CorrespondenceFunctionActions.updateCorrespondenceFunctionFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(CorrespondenceFunctionActions.removeCorrespondenceFunction, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    CorrespondenceFunctionActions.removeCorrespondenceFunctionSuccess,
    (state, { ids }) =>
      correspondenceFunctionAdapter.removeMany(ids, { ...state, loaded: false })
  ),
  on(
    CorrespondenceFunctionActions.removeCorrespondenceFunctionFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: CorrespondenceFunctionState | undefined,
  action: Action
) {
  return correspondenceFunctionReducer(state, action);
}
